import EntityMember from "_shared/entity-members/entity-member";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import { withoutFeeScheduleTeamMember } from "_shared/fee-schedules/categories/fee-schedule-template-category";
import Guid from "_shared/guids/guid";
import type Session from "users/__session/session";
import { FeeScheduleCategoryMaskedCurrency, Masked } from "work/masked-fields";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import ProposalTeamReuse from "work/proposals/drafts-from-previous/proposal-team-reuse";
import Proposal from "work/proposals/proposal";
import WorkDocument from "work/work-agreements/contracts/work-document";
import ProjectName from "work/work-agreements/project-name/project-name";
import ClientTeam from "work/work-agreements/teams/client-team/client-team";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";

/** What a new draft started from a previous proposal holds, and what it could not take. */
export type DraftFromPreviousProposal = {
  builder: ProposalBuilder;
  /** People on the source's teams who have left the organization they were on the team for. */
  peopleWhoLeft: EntityMember[];
  /** Team leaders still at their organization who can no longer sign for it. */
  leadersWhoCannotSign: EntityMember[];
  /** Documents the source names that no longer exist, so there is nothing to copy. */
  unavailableDocuments: WorkDocument[];
};

/**
 * Builds a new, unsaved draft from a proposal the user can read (#1569).
 *
 * The draft is a fresh proposal, not a revision, so it takes the content and none of the history:
 * no dates, reviewers, comments, status or negotiation. It takes what the reader can see and nothing
 * more. A field masked for them stays empty, because a masked value is a placeholder and copying a
 * placeholder would save something they never saw. Their side of the proposal comes from their own
 * session, which the builder already reads; a firm copying a proposal it received is still the firm.
 *
 * People are carried over only while they can still be: anybody who has left the organization they
 * were on the team for is dropped, and so is a leader who can no longer sign for it, since the server
 * refuses a draft led by somebody who cannot. A rate naming a dropped person stops naming them.
 *
 * Documents are named, not linked. Each is marked to be copied on the first save, which is when the
 * server makes a copy with its own id; until then the draft shows the original, and an abandoned
 * draft leaves no copies behind.
 */
export function buildDraftFromPreviousProposal(args: {
  proposal: Proposal;
  session: Session;
  teamReuse: ProposalTeamReuse;
}): DraftFromPreviousProposal {
  const { proposal, session, teamReuse } = args;
  let builder = new ProposalBuilder({ session });
  const peopleWhoLeft: EntityMember[] = [];
  const leadersWhoCannotSign: EntityMember[] = [];
  const noteDropped = (person: EntityMember) => {
    if (teamReuse.hasLeft(person)) addOnce(peopleWhoLeft, person);
    else addOnce(leadersWhoCannotSign, person);
  };

  // The source's name as it is, with no "Copy of" in front. A draft sent to several firms gives every
  // firm its name, and the firms' proposals are one request under one name (#6460).
  const name = proposal.name?.value;
  if (name) builder = builder.setName(new ProjectName(name));
  if (proposal.description?.value) builder = builder.setDescription(proposal.description);
  builder = builder.setNegotiable(proposal.negotiable.value);

  // #region Teams
  const clientTeam = proposal.clientTeam;
  if (clientTeam && Masked.isNotMasked(clientTeam)) {
    builder = withClientTeam(builder, clientTeam, teamReuse, noteDropped);
  }

  if (Masked.isPresentNotMasked(proposal.teamRestricted)) {
    builder = builder.setTeamRestricted(proposal.teamRestricted);
  }

  const vendorTeam = proposal.vendorTeam;
  const feeSchedule = proposal.feeSchedule;
  builder = withVendorTeamAndFeeSchedule({
    builder,
    team: vendorTeam && Masked.isNotMasked(vendorTeam) ? vendorTeam : undefined,
    feeSchedule: Masked.isNotMasked(feeSchedule)
      ? (feeSchedule.map(withoutMaskedTeamMember) as FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[])
      : undefined,
    teamReuse,
    noteDropped,
  });
  // #endregion

  if (Masked.isPresentNotMasked(proposal.discount)) builder = builder.setDiscount(proposal.discount);

  // #region Templates
  // Only the reader's own side's lists, the side their session is on: the server lets a party write
  // no other one.
  if (session.context?.viewingAsVendor) {
    builder = builder
      .setVendorTeamTemplateIds(cloneIds(proposal.vendorTeamTemplateIds))
      .setVendorFeeScheduleTemplateIds(cloneIds(proposal.vendorFeeScheduleTemplateIds));
  } else {
    builder = builder
      .setClientTeamTemplateIds(cloneIds(proposal.clientTeamTemplateIds))
      .setClientFeeScheduleTemplateIds(cloneIds(proposal.clientFeeScheduleTemplateIds));
  }
  // #endregion

  // #region Documents
  const unavailableDocuments: WorkDocument[] = [];
  const toCopy = (documents: WorkDocument[]): WorkDocument[] =>
    documents.flatMap((document) => {
      if (document.isAvailable === false) {
        unavailableDocuments.push(document);
        return [];
      }
      return [document.toBeCopiedOnSave()];
    });

  const conflictsCheckWaived = proposal.conflictsCheckWaived;
  if (Masked.isPresentNotMasked(conflictsCheckWaived)) {
    builder = builder.setConflictsCheckWaived(conflictsCheckWaived);
  }
  const conflicts = proposal.conflicts;
  if (Masked.isNotMasked(conflicts) && conflicts.length > 0) builder = builder.setConflicts(toCopy(conflicts));
  const clientPolicies = proposal.clientPolicies;
  if (Masked.isNotMasked(clientPolicies) && clientPolicies.length > 0) {
    builder = builder.setClientPolicies(toCopy(clientPolicies));
  }
  const vendorPolicies = proposal.vendorPolicies;
  if (Masked.isNotMasked(vendorPolicies) && vendorPolicies.length > 0) {
    builder = builder.setVendorPolicies(toCopy(vendorPolicies));
  }
  // #endregion

  return { builder, peopleWhoLeft, leadersWhoCannotSign, unavailableDocuments };
}

/**
 * Seats the source's client team, less anybody who cannot be carried over.
 *
 * A Client Representative who cannot be carried over leaves the seat to whoever the new builder put
 * in it, which is the creator when they can sign and nobody otherwise. Shared with a draft started
 * from a saved proposal template (#6129).
 */
export function withClientTeam(
  builder: ProposalBuilder,
  team: ClientTeam,
  teamReuse: ProposalTeamReuse,
  noteDropped: (person: EntityMember) => void,
): ProposalBuilder {
  const members = team.members.filter((member) => {
    if (!teamReuse.hasLeft(member)) return true;
    noteDropped(member);
    return false;
  });

  let leader = team.leader;
  if (leader && !teamReuse.canLead(leader)) {
    noteDropped(leader);
    leader = undefined;
  }
  const defaultTeam = builder.currentSpec.workAgreement.clientTeam;
  leader ??= Masked.isPresentNotMasked(defaultTeam) ? defaultTeam.leader : undefined;

  if (leader) return builder.setClient(leader).setClientTeamMembers(members);
  if (members.length === 0) return builder;
  // Members are kept without a representative, for the user to seat one. The builder adds members
  // only beneath a representative, so one is seated just long enough to add them.
  return builder.setClient(members[0]).setClientTeamMembers(members).setClient(undefined);
}

/**
 * Seats a vendor team and the fee schedule its rates are staffed from, less anybody who cannot be
 * carried over. Shared with a draft started from a saved proposal template (#6129).
 *
 * The whole team goes in first, so the fee schedule lands on the team that its rates name. Seating
 * the team without its departed people afterwards is what makes the rates naming them stop, the same
 * way removing somebody from a draft's team does (#5561).
 */
export function withVendorTeamAndFeeSchedule(args: {
  builder: ProposalBuilder;
  team: VendorTeam | undefined;
  feeSchedule: FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[] | undefined;
  teamReuse: ProposalTeamReuse;
  noteDropped: (person: EntityMember) => void;
}): ProposalBuilder {
  const { team, feeSchedule, teamReuse, noteDropped } = args;
  let builder = args.builder;
  if (team) builder = builder.setVendorTeam(withoutDuplicateLeader(team));
  if (feeSchedule && feeSchedule.length > 0) builder = builder.setFeeSchedule(feeSchedule);
  if (team) {
    const seatedTeam = vendorTeamWithoutDropped(builder, team, teamReuse, noteDropped);
    builder = builder.setVendorTeam(seatedTeam ?? emptyVendorTeam(team));
  }
  return builder;
}

function vendorTeamWithoutDropped(
  builder: ProposalBuilder,
  team: VendorTeam,
  teamReuse: ProposalTeamReuse,
  noteDropped: (person: EntityMember) => void,
): VendorTeam | undefined {
  const members = team.members.filter((member) => {
    if (team.isLedBy(member) || !teamReuse.hasLeft(member)) return true;
    noteDropped(member);
    return false;
  });

  let leader = team.leader;
  if (leader && !teamReuse.canLead(leader)) {
    noteDropped(leader);
    leader = undefined;
  }
  if (!leader) {
    // A firm's new draft starts led by the firm's creator when they can sign (#5658). Keeping that
    // is what keeps the draft openable by the person who made it.
    const builderLeader = new ProposalBuilder({ session: builder.session }).currentSpec.workAgreement.vendorTeam;
    leader = Masked.isPresentNotMasked(builderLeader) ? builderLeader.leader : undefined;
  }

  if (!leader && members.length === 0) return undefined;
  return new VendorTeam({
    leader,
    members: members.filter((member) => !member.isSamePersonAs(leader)),
    marketplaceId: team.marketplaceId,
  });
}

/** A vendor team with nobody on it, which the builder reads as no team at all. */
function emptyVendorTeam(team: VendorTeam): VendorTeam {
  return new VendorTeam({ members: [], marketplaceId: team.marketplaceId });
}

function withoutDuplicateLeader(team: VendorTeam): VendorTeam {
  return new VendorTeam({
    leader: team.leader,
    members: team.members.filter((member) => !team.isLedBy(member)),
    marketplaceId: team.marketplaceId,
  });
}

/**
 * A rate whose person is hidden from the reader is copied without one. Copying the hidden marker
 * would save a rate naming somebody the reader never saw.
 */
function withoutMaskedTeamMember(
  category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency,
): FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency {
  return category.teamMemberIsMasked ? withoutFeeScheduleTeamMember(category) : category;
}

function cloneIds(ids: Guid[]): Guid[] {
  return ids.map((id) => id.clone());
}

function addOnce(people: EntityMember[], person: EntityMember) {
  if (!people.some((other) => other.isEqualTo(person))) people.push(person);
}
